"use client";

import { expandingMenuStyles } from "./styles/expanding-menu.styles";
import { createContext, useContext, useId, useCallback } from "react";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useKeypress } from "../../hooks";

import type { ExpandingMenuContextProps, ExpandingMenuProps } from "./types/expanding-menu.types";

export const ExpandingMenuContext = createContext<ExpandingMenuContextProps>({
  value: "",
  disabled: false,
  expanded: false,
  toggleExpanded: () => {},
  onExpandedChange: () => {},
});

export const ExpandingMenuProvider: React.FC<ExpandingMenuProps> = (props) => {
  const id = useId();
  const {
    children,
    className,
    value = id,
    disabled,
    expanded: expandedProp,
    defaultExpanded: defaultExpandedProp = false,
    onExpandedChange,
  } = props;

  const handleExpandedChange = useCallback(
    (value: boolean | undefined) => {
      onExpandedChange?.(!!value);
    },
    [onExpandedChange],
  );

  const [expanded, setExpanded] = useControllableState<boolean | undefined>({
    defaultProp: defaultExpandedProp,
    prop: expandedProp,
    onChange: handleExpandedChange,
  });

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  useKeypress("Escape", () => {
    setExpanded(false);
  });

  const styles = expandingMenuStyles({ className, expanded });

  return (
    <ExpandingMenuContext.Provider value={{ value, disabled, expanded, toggleExpanded, onExpandedChange }}>
      <div className={styles}>{children}</div>
    </ExpandingMenuContext.Provider>
  );
};

export const useExpandingMenu = () => {
  return useContext(ExpandingMenuContext);
};
